import { useMemo, useState } from 'react';

import { Button } from './Button';

type Primitive = string | number | boolean | null | undefined;
type Row = Record<string, Primitive>;

export interface TablePreviewProps {
  /** Ordered column headers. Determines column order; extras in rows are ignored. */
  headers: string[];
  /** Rows as flat objects keyed by header. */
  rows: Row[];
  /** Optional columns to highlight (e.g. PII or ambiguous). */
  highlight?: {
    pii?: string[];
    ambiguous?: string[];
  };
  /**
   * Called when the reviewer confirms a reveal of PII cells. Intended as
   * an audit hook — caller can log the action, POST a backend audit
   * entry, etc. Return `false` (or a rejected promise) to cancel the
   * reveal. v0.1 has no backend audit endpoint for PII reveals; that's
   * scoped to v0.2. For now the only side effect of omitting this is
   * that reveals aren't audited at all.
   */
  onRequestReveal?: () => boolean | Promise<boolean>;
  /** Empty-state copy if rows is zero-length. */
  emptyMessage?: string;
  /** Max height in pixels before the body scrolls. Default 360. */
  maxBodyHeight?: number;
  className?: string;
}

type SortDir = 'asc' | 'desc';

/**
 * Scrollable, sortable preview table.
 *
 * Deliberately *not* a data grid — we get a few hundred preview rows at most,
 * so plain <table> with sticky headers and a client-side sort is enough.
 *
 * Column highlighting uses inline pill tags in the header so reviewers can
 * instantly see which fields RAS flagged as PII or ambiguous.
 *
 * PII behaviour
 * -------------
 * Columns listed in `highlight.pii` render their cells with a CSS blur +
 * `user-select: none` by default. A "Reveal PII" toggle above the table
 * flips all PII columns to plaintext after the reviewer confirms. Blur is
 * a visual gate only — DevTools can still inspect the DOM, which is
 * acceptable for v0.1 (the reviewer UI is behind auth in production).
 * v0.2 adds a proper backend audit entry on reveal.
 */
export function TablePreview({
  headers,
  rows,
  highlight,
  onRequestReveal,
  emptyMessage = 'No rows to preview.',
  maxBodyHeight = 360,
  className = '',
}: TablePreviewProps) {
  const [sort, setSort] = useState<{ col: string; dir: SortDir } | null>(null);
  const [piiRevealed, setPiiRevealed] = useState(false);
  const [revealPending, setRevealPending] = useState(false);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const { col, dir } = sort;
    const factor = dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => compare(a[col], b[col]) * factor);
  }, [rows, sort]);

  if (rows.length === 0) {
    return (
      <div
        className={`rounded-md border border-border bg-surface-alt/30 px-4 py-6 text-sm text-muted ${className}`}
      >
        {emptyMessage}
      </div>
    );
  }

  const piiSet = new Set(highlight?.pii ?? []);
  const ambigSet = new Set(highlight?.ambiguous ?? []);
  const hasPii = piiSet.size > 0;

  const onSort = (col: string) => {
    setSort((prev) => {
      if (!prev || prev.col !== col) return { col, dir: 'asc' };
      if (prev.dir === 'asc') return { col, dir: 'desc' };
      return null;
    });
  };

  const onToggleReveal = async () => {
    if (piiRevealed) {
      // Re-blur is always allowed without confirmation.
      setPiiRevealed(false);
      return;
    }
    setRevealPending(true);
    try {
      let allowed = true;
      if (onRequestReveal) {
        const result = await onRequestReveal();
        allowed = result !== false;
      } else {
        // No audit hook supplied — fall back to a native confirm so the
        // reveal still requires an explicit reviewer acknowledgement.
        allowed = window.confirm(
          'Reveal PII values in this preview? This action is visible to anyone watching your screen and (in v0.2) will be audit-logged.',
        );
      }
      if (allowed) setPiiRevealed(true);
    } finally {
      setRevealPending(false);
    }
  };

  return (
    <div className={className}>
      {hasPii && (
        <div className="mb-2 flex items-center gap-3 text-xs">
          <span className="text-muted">
            {piiRevealed
              ? 'PII columns are visible — click to re-blur.'
              : 'PII columns are blurred. Click to reveal after confirming.'}
          </span>
          <Button
            variant={piiRevealed ? 'ghost' : 'secondary'}
            onClick={onToggleReveal}
            disabled={revealPending}
          >
            {revealPending
              ? '…'
              : piiRevealed
                ? 'Hide PII'
                : 'Reveal PII'}
          </Button>
        </div>
      )}
      <div
        className="overflow-auto rounded-md border border-border bg-paper"
        style={{ maxHeight: maxBodyHeight }}
        role="region"
        aria-label="Table preview"
      >
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-surface-alt/60 backdrop-blur">
          <tr>
            {headers.map((h) => {
              const isActive = sort?.col === h;
              const indicator = isActive ? (sort?.dir === 'asc' ? ' ▲' : ' ▼') : '';
              return (
                <th
                  key={h}
                  scope="col"
                  className="text-left font-medium text-xs uppercase tracking-wide text-muted px-3 py-2 border-b border-border whitespace-nowrap"
                >
                  <button
                    type="button"
                    onClick={() => onSort(h)}
                    className="inline-flex items-center gap-1 hover:text-ink"
                    aria-sort={isActive ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <span>{h}</span>
                    {indicator && <span aria-hidden>{indicator}</span>}
                  </button>
                  {piiSet.has(h) && <Tag tone="danger">PII</Tag>}
                  {ambigSet.has(h) && <Tag tone="warn">ambiguous</Tag>}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={i} className="odd:bg-surface-alt/20">
              {headers.map((h) => {
                const blur = piiSet.has(h) && !piiRevealed;
                return (
                  <td
                    key={h}
                    className="px-3 py-2 align-top border-b border-border/50"
                    // When blurred: CSS blur + disable text selection so
                    // the reviewer can't copy the obscured value without
                    // first going through the reveal flow.
                    style={
                      blur
                        ? {
                            filter: 'blur(6px)',
                            userSelect: 'none',
                            transition: 'filter 120ms',
                          }
                        : { transition: 'filter 120ms' }
                    }
                    aria-hidden={blur || undefined}
                    data-pii-blurred={blur || undefined}
                  >
                    {formatCell(r[h])}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// helpers
// ----------------------------------------------------------------------------

function Tag({ tone, children }: { tone: 'danger' | 'warn'; children: React.ReactNode }) {
  const cls =
    tone === 'danger'
      ? 'bg-danger/10 text-danger'
      : 'bg-warn/10 text-warn';
  return (
    <span className={`ml-2 inline-block rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase ${cls}`}>
      {children}
    </span>
  );
}

function formatCell(v: Primitive): React.ReactNode {
  if (v === null || v === undefined || v === '') {
    return <span className="text-muted italic">—</span>;
  }
  return String(v);
}

function compare(a: Primitive, b: Primitive): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return -1;
  if (b === null || b === undefined) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}
